import React from "react";
import Drawer from "@mui/material/Drawer";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import EmployeeProfileTabHeader from "./EmployeeProfileTabHeader";
import CloseIcon from "@mui/icons-material/Close";
import { PurpleSwitch } from "../../../../../helpers/helpers";

// Match the outlined fields used elsewhere in the employee profile.
export const schedulingFieldSx = {
  "& .MuiInputBase-root, & .MuiInputLabel-root, & .MuiFormHelperText-root": { fontFamily: "inherit", fontSize: "14px", letterSpacing: "normal" },
  "& .MuiFormHelperText-root": { marginLeft: 0, marginRight: 0, marginTop: "6px", lineHeight: 1.5 },
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: "white",
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#6d28d9" },
    "&.Mui-focused fieldset": { borderColor: "#6d28d9" },
    "&.Mui-error fieldset": { borderColor: "#dc2626" },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: "#6d28d9" },
  "& .MuiInputLabel-root.Mui-error": { color: "#dc2626" },
};

function PreferenceHelp({ label, children }) {
  return (
    <Tooltip title={children} arrow placement="bottom" describeChild enterTouchDelay={0}
      componentsProps={{ tooltip: { sx: { fontFamily: "inherit", fontSize: "13px", letterSpacing: "normal", lineHeight: 1.5, padding: "12px", maxWidth: "300px" } } }}>
      <IconButton type="button" size="small" aria-label={label} sx={{ color: "#64748b", flexShrink: 0 }}>
        <InfoOutlinedIcon sx={{ fontSize: "18px" }} />
      </IconButton>
    </Tooltip>
  );
}

export function employeeSchedulingPayload(member, changes) {
  const roles = member.available_role_info ?? member.roles_and_role_types;
  if (!Array.isArray(roles)) throw new Error("Employee details are unavailable. Close and reopen the employee before saving.");
  // The existing endpoint requires these attributes; preserve them unchanged.
  return {
    id: member.id,
    hourly_wage: member.hourly_wage,
    min_weekly_hours: member.min_weekly_hours,
    max_weekly_hours: member.max_weekly_hours,
    available_role_info: roles.map((role) => ({ ...role, store_team_member: member.id })),
    ...changes,
  };
}

export default function EmployeeSchedulingPreferences({ open, member, onClose, onSave, returnFocusRef, inline = false }) {
  const id = React.useId();
  const [target, setTarget] = React.useState("");
  const [priority, setPriority] = React.useState(false);
  const [minimum, setMinimum] = React.useState("");
  const [errors, setErrors] = React.useState({});
  const [saveError, setSaveError] = React.useState("");
  const [saved, setSaved] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const targetRef = React.useRef(null);
  const minimumRef = React.useRef(null);

  React.useEffect(() => {
    if (open && member) {
      setTarget(member.target_weekly_hours ?? "");
      setPriority(Boolean(member.prioritize_target_hours) && Number(member.target_weekly_hours) > 0);
      setMinimum(member.preferred_min_paid_shift_hours ?? "");
      setErrors({});
      setSaveError("");
      setSaved(false);
    }
    // Refreshes of the same employee must not overwrite an unsaved draft.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, member?.id]);

  const close = () => { if (!saving) onClose(); };
  const submit = async (event) => {
    event.preventDefault();
    if (saving) return;
    const paidTarget = String(target).trim() === "" ? null : Number(target);
    const paidMinimum = String(minimum).trim() === "" ? null : Number(minimum);
    const nextErrors = {
      target: paidTarget !== null && !Number.isFinite(paidTarget),
      minimum: paidMinimum !== null && (!Number.isFinite(paidMinimum) || paidMinimum <= 0 || paidMinimum > 24 || paidMinimum * 4 !== Math.round(paidMinimum * 4)),
    };
    setErrors(nextErrors);
    if (nextErrors.target || nextErrors.minimum) {
      (nextErrors.target ? targetRef : minimumRef).current?.focus();
      return;
    }
    setSaved(false);
    setSaving(true);
    setSaveError("");
    try {
      const saved = await onSave(employeeSchedulingPayload(member, {
        target_weekly_hours: paidTarget,
        prioritize_target_hours: priority && paidTarget > 0,
        preferred_min_paid_shift_hours: paidMinimum,
      }));
      if (saved !== true) throw new Error("Preferences could not be saved. Your changes are still here; try again.");
      if (inline) setSaved(true);
      else onClose();
    } catch (error) {
      setSaveError(error.message || "Preferences could not be saved. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const form = (
      <form onSubmit={submit} onChange={() => setSaved(false)} noValidate className={inline ? "w-full min-w-0 overflow-hidden rounded-lg border border-slate-200 bg-white" : "flex h-full w-[450px] max-w-[100vw] flex-col overflow-hidden"}>
        {!inline && <div className="flex shrink-0 items-start justify-between gap-3 border-b border-slate-200 px-6 py-5">
          <div>
            <div className="flex items-center gap-1">
              <h2 id={`${id}-title`} className="text-lg font-semibold text-slate-900">Scheduling preferences</h2>
              <PreferenceHelp label="How preferences apply">
                Autoschedule, AutoTune, and AutoFill reuse these preferences. Availability, leave, legal limits, skills, coverage, and locks still apply; targets aren’t guaranteed.
              </PreferenceHelp>
            </div>
            <p className="mt-1 text-sm text-slate-500">{member?.first_name} {member?.last_name}</p>
          </div>
          <IconButton type="button" aria-label="Close scheduling preferences" onClick={close} disabled={saving} sx={{ color: "#64748b" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>}
        <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-6 py-5">
          {!inline && <p className="text-sm leading-5 text-slate-500">Saved at this location for future scheduling runs.</p>}
          <div>
            <div className="mb-2 flex items-center gap-1">
              <label htmlFor={`${id}-target`} className="text-sm font-medium text-slate-900">Weekly target hours</label>
              <PreferenceHelp label="About weekly target hours">Paid hours per week, excluding unpaid breaks. Leave blank for no target.</PreferenceHelp>
            </div>
          <TextField id={`${id}-target`} fullWidth placeholder="No target" type="number" size="small" autoFocus={!inline} inputProps={{ step: "any" }}
            value={target} inputRef={targetRef} sx={schedulingFieldSx} disabled={saving} error={Boolean(errors.target)}
            helperText={errors.target ? "Enter a finite number, or leave blank for no target." : undefined}
            onChange={(event) => {
              setTarget(event.target.value);
              if (!(Number(event.target.value) > 0)) setPriority(false);
              setErrors((current) => ({ ...current, target: false }));
            }} />
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-1">
                <label htmlFor={`${id}-priority`} className="text-sm font-medium text-slate-900">Prioritize weekly target</label>
                <PreferenceHelp label="About prioritizing weekly target">Meet this target before the labor budget. May exceed budget.</PreferenceHelp>
              </div>
              <Tooltip title={!(Number(target) > 0) ? "Set a positive weekly target to turn this on." : ""}
                arrow placement="bottom" describeChild enterTouchDelay={0}
                componentsProps={{ tooltip: { sx: { fontFamily: "inherit", fontSize: "13px", letterSpacing: "normal", lineHeight: 1.5, padding: "12px", maxWidth: "300px" } } }}>
                <span className="inline-flex shrink-0 rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-violet-700"
                  tabIndex={!(Number(target) > 0) ? 0 : undefined}
                  aria-label={!(Number(target) > 0) ? "Why prioritize weekly target is unavailable" : undefined}>
                  <PurpleSwitch id={`${id}-priority`} checked={priority && Number(target) > 0} disabled={!(Number(target) > 0) || saving}
                    inputProps={{ "aria-label": "Prioritize this weekly target" }}
                    onChange={(event) => setPriority(event.target.checked)} />
                </span>
              </Tooltip>
            </div>
          </div>
          <div>
            <div className="mb-2 flex items-center gap-1">
              <label htmlFor={`${id}-minimum`} className="text-sm font-medium text-slate-900">Preferred minimum shift hours</label>
              <PreferenceHelp label="About preferred minimum shift hours">Preferred paid hours per shift, excluding unpaid breaks. Shorter shifts are still allowed. Leave blank for no preference.</PreferenceHelp>
            </div>
          <TextField id={`${id}-minimum`} fullWidth placeholder="No minimum" type="number" size="small"
            value={minimum} inputRef={minimumRef} sx={schedulingFieldSx} disabled={saving} error={Boolean(errors.minimum)}
            inputProps={{ min: 0.25, max: 24, step: 0.25 }}
            helperText={errors.minimum ? "Enter 0.25 to 24 hours in quarter-hour increments, or leave blank." : undefined}
            onChange={(event) => { setMinimum(event.target.value); setErrors((current) => ({ ...current, minimum: false })); }} />
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap justify-end gap-3 border-t border-slate-200 bg-white px-6 py-4">
          {saveError && <p role="alert" className="w-full rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{saveError}</p>}
          {saved && <p role="status" className="w-full text-sm text-emerald-700">Scheduling preferences saved.</p>}
          {!inline && <Button type="button" variant="outlined" disabled={saving} onClick={close}
            sx={{ fontFamily: "inherit", textTransform: "none", borderRadius: "8px", paddingX: "16px", paddingY: "8px", color: "#475569", borderColor: "#cbd5e1" }}>Cancel</Button>}
          <Button type="submit" variant="contained" disableElevation disabled={saving}
            sx={{ fontFamily: "inherit", textTransform: "none", borderRadius: "8px", paddingX: "20px", paddingY: "8px", backgroundColor: "#6d28d9", "&:hover": { backgroundColor: "#5b21b6" } }}>
            {saving ? "Saving…" : "Save preferences"}
          </Button>
        </div>
      </form>
  );

  if (inline) return (
    <div className="max-w-5xl mx-auto">
      <EmployeeProfileTabHeader icon={TuneIcon} title="Scheduling Preferences"
        subtitle="Manage weekly targets and preferred shift length" />
      {form}
    </div>
  );

  return (
    <Drawer open={open} anchor="right" onClose={close} sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
      PaperProps={{ role: "dialog", "aria-modal": true, "aria-labelledby": `${id}-title` }}
      SlideProps={{ onExited: () => returnFocusRef?.current?.focus() }}>
      {form}
    </Drawer>
  );
}
